import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api'
import { MapPin, PackageX, CheckCircle } from 'lucide-react'
import { publicApi } from '../../services/api'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'

// Configuração do mapa - mesmo padrão de RecipientForm.jsx
const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px'
}

// Centro padrão: Manaus - mesmo fallback de RecipientForm.jsx, usado quando
// o destinatário ainda não tem coordenada nenhuma
const defaultCenter = {
  lat: -3.1190,
  lng: -60.0217
}

// Página pública (sem login) que o destinatário abre a partir do link
// enviado pelo vendedor via WhatsApp, no 1º pedido, pra confirmar/ajustar o
// pino da própria entrega. Sem MainLayout/Sidebar de propósito - mesmo
// padrão do DriverLinkView (BUG-15): tela isolada, pensada pra abrir direto
// no navegador do celular.
export default function PinConfirmView() {
  const { token } = useParams()
  const [position, setPosition] = useState(null)
  const [confirmed, setConfirmed] = useState(false)
  const [mutationFailed, setMutationFailed] = useState(false)

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
  })

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pinConfirm', token],
    queryFn: async () => {
      const response = await publicApi.get(`/api/pin-confirm/${token}`)
      return response.data.data
    },
    retry: false,
  })

  // Só inicializa a posição do marker na primeira vez que os dados chegam
  useEffect(() => {
    if (data && !position) {
      setPosition(data.coordinates || defaultCenter)
    }
  }, [data, position])

  const confirmPin = useMutation({
    mutationFn: () => publicApi.post(`/api/pin-confirm/${token}`, position),
    onSuccess: () => setConfirmed(true),
    onError: () => setMutationFailed(true),
  })

  const onMarkerDragEnd = useCallback((e) => {
    setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() })
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // O backend não distingue token inválido, expirado ou já confirmado - os
  // três casos voltam como 404, de propósito. Mesma tela genérica do
  // DriverLinkView (inclusive pra falha na confirmação em si) - não vazar o
  // motivo aqui.
  if (isError || !data || mutationFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <PackageX className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Este link não está mais disponível</p>
          <p className="text-sm text-gray-500 mt-1">Fale com quem te enviou o link pra conseguir um novo.</p>
        </div>
      </div>
    )
  }

  if (confirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium">Localização confirmada, obrigado!</p>
        </div>
      </div>
    )
  }

  const { address } = data

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <h1 className="text-lg font-semibold text-gray-900">Confirme sua localização</h1>
      </div>

      <div className="p-4 space-y-3">
        <Card padding="sm">
          <p className="font-medium text-gray-900">{data.name}</p>
          <p className="text-sm text-gray-500 flex items-start gap-1 mt-1">
            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              {address.street}, {address.number}
              {address.complement ? ` - ${address.complement}` : ''} - {address.neighborhood}, {address.city}
            </span>
          </p>
        </Card>

        <p className="text-sm text-gray-600">
          Ajuste o pino pra sua localização exata e confirme.
        </p>

        {isLoaded && position && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={position}
            zoom={17}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
          >
            <Marker position={position} draggable={true} onDragEnd={onMarkerDragEnd} />
          </GoogleMap>
        )}
      </div>

      <div className="px-4">
        <Button
          fullWidth
          onClick={() => confirmPin.mutate()}
          isLoading={confirmPin.isPending}
        >
          Confirmar localização
        </Button>
      </div>
    </div>
  )
}
